import React, { useEffect } from 'react';
import AddResourceForm from './AddResourceForm';
import ResourcesSearchTab from './ResourcesSearchTab';
import { useFieldArray, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import ResourcePageValidator from '../validators/resourcesPageValidator';
import { permissionOptions } from '../constants/resourcePermissionOptions';
import EditResourceCards from './EditResourceCards';
import { useIsSubmitDisabledStore } from '../../shared/stores/FormSubmitBtnDisabledStore';
import { EDIT_RESOURCE_FORM } from '@shared/constants/editResourceFormKey';
import { SEARCH_TAB } from '../constants/resourceTabs';
import { useFilterResourcesByTab } from '../hooks/useFilterResourcesByTab';
import { useShallow } from 'zustand/shallow';

const FORM_FIELD_NAME = 'resources';

/**
 * @typedef {object} EditResourcesSectionProps
 * @property {Array<*>} resources
 * @property {(data: { resources: Array<{ category: number, title: string, description: string, link: string, permissions: Array<{ label: string, value: number, displayName?: string }> }> }) => void | Promise<void>} handleFormSubmit
 * @property {string} activeTab
 */

/**
 * @param {EditResourcesSectionProps} props
 */
const EditResourcesSection = props => {
  const { resources, activeTab, handleFormSubmit } = props;

  const setIsSubmitDisabled = useIsSubmitDisabledStore(
    useShallow(state => state.setIsSubmitDisabled)
  );

  const isSearchTab = SEARCH_TAB.value === activeTab;

  const form = useForm({
    resolver: joiResolver(ResourcePageValidator.editSchema),
    defaultValues: getDefaultFormValues(resources)
  });

  const { handleSubmit, control, formState, watch } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: FORM_FIELD_NAME
  });

  useEffect(() => {
    setIsSubmitDisabled(EDIT_RESOURCE_FORM, !formState.isDirty);
  }, [formState.isDirty, setIsSubmitDisabled]);

  const watchedResources = watch(FORM_FIELD_NAME);

  useFilterResourcesByTab(watchedResources, activeTab);

  return (
    <>
      {isSearchTab && <ResourcesSearchTab resources={watchedResources} />}
      <div>
        <form
          id={EDIT_RESOURCE_FORM}
          className='edit-resource-form'
          onSubmit={handleSubmit(handleFormSubmit)}>
          <EditResourceCards formFields={fields} control={control} remove={remove} />
        </form>
        <AddResourceForm handleAddNewResource={append} />
      </div>
    </>
  );
};

const getDefaultFormValues = resources => ({
  [FORM_FIELD_NAME]: resources.map(x => ({
    ...x,
    permissions: permissionOptions.filter(options => x.permissions.includes(options.value))
  }))
});

export default EditResourcesSection;
